import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
// Force inject locale từ src/locales vào pro-layout
import './components/ProLayoutForceInjector';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { syncTranslations } from '@/services/translation';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getPendingApprovalsCount } from '@/services/mining';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev =
  process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  pendingApprovalsCount?: number;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // Kiểm tra session token (cho lần đăng nhập hiện tại) HOẶC remember token
  const { location } = history;
  const sessionToken = sessionStorage.getItem('cow_session_token');
  const rememberToken = localStorage.getItem('cow_remember_token');

  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    // Cho phép truy cập nếu có session token HOẶC remember token
    if (sessionToken || rememberToken) {
      const currentUser = await fetchUserInfo();
      const pendingApprovalsCount = await getPendingApprovalsCount();
      return {
        fetchUserInfo,
        currentUser,
        pendingApprovalsCount,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    } else {
      // Không có token nào, redirect về login
      history.push(loginPath);
    }
  }
  // Đồng bộ bản dịch từ database
  try {
    await syncTranslations();
  } catch (e) {
    console.error('Failed to sync translations:', e);
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: '', // Ẩn title default
      render: (_, avatarChildren) => {
        return <AvatarDropdown />;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.name,
    // },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>AI Support</span>
        </Link>,
      ]
      : [],
    menuItemRender: (menuItemProps, defaultDom) => {
      if (menuItemProps.name === 'approvals' && initialState?.pendingApprovalsCount) {
        return (
          <Link to={menuItemProps.path!}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{defaultDom}</span>
              <span style={{ 
                background: '#ff4d4f', 
                color: '#fff', 
                borderRadius: '10px', 
                padding: '0 6px', 
                fontSize: '11px',
                height: '18px',
                lineHeight: '18px',
                minWidth: '18px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                {initialState.pendingApprovalsCount}
              </span>
            </div>
          </Link>
        );
      }
      if (menuItemProps.isUrl || !menuItemProps.path) {
        return defaultDom;
      }
      return <Link to={menuItemProps.path}>{defaultDom}</Link>;
    },
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: 'http://localhost:8000/',
  ...errorConfig,
};
